// =====================================================
// EVENT PAYMENTS SERVICE
// Payments ledger (event_payments) — every payment and reversal is kept.
// Writes go through RPCs (20261005000004_events_payments_ledger.sql), which
// also refresh the participant's cached payment_status / payment_amount.
// =====================================================

import { supabase } from '@/supabase.config';
import type { EventPaymentMethod } from '@/models/eventPaymentMethods';

// RPCs / table not in generated types until regen
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

// ===================
// TYPES
// ===================

export type { EventPaymentMethod } from '@/models/eventPaymentMethods';
export { PAYMENT_METHOD_LABELS, SELECTABLE_PAYMENT_METHODS, normalizePaymentMethod } from '@/models/eventPaymentMethods';

export interface RecordPaymentData {
  participant_id: string;
  payment_amount: number;
  method?: EventPaymentMethod | null;
  notes?: string;
  voucher_key?: string | null;
  /** @deprecated recorded_by is set server-side from the session */
  paid_by?: string;
}

export interface ReversePaymentData {
  participant_id: string;
  reversal_reason?: string;
  /** @deprecated reversed_by is set server-side from the session */
  reversed_by?: string;
}

export interface EventPayment {
  id: string;
  event_id: string;
  participant_id: string;
  amount: number;
  method: EventPaymentMethod | null;
  status: 'RECORDED' | 'REVERSED';
  paid_at: string;
  recorded_by: string | null;
  receipt_id: string | null;
  notes: string | null;
  reversed_at: string | null;
  reversal_reason: string | null;
  participant?: {
    id: string;
    calculated_price: number | null;
    minister_id: string | null;
    public_token: string;
    registration_data: Record<string, unknown> | null;
    minister?: { full_name: string | null; adb_code: string; dni: string; cell_phone: string | null } | null;
    guest?: { full_name: string; dni: string | null; phone: string | null } | null;
  } | null;
  recorder?: { full_name: string | null } | null;
  receipt?: { id: string; number: number; year: number; status: string } | null;
}

export interface BulkResult {
  success: number;
  failed: number;
  errors: string[];
}

// ===================
// RECORD / REVERSE
// ===================

/** Record a payment (full or partial). Returns the ledger entry id. */
export async function recordPayment(data: RecordPaymentData): Promise<string> {
  const { data: id, error } = await db.rpc('record_event_payment', {
    p_participant_id: data.participant_id,
    p_amount: data.payment_amount,
    p_method: data.method ?? null,
    p_notes: data.notes ?? null,
    p_voucher_key: data.voucher_key ?? null,
  });
  if (error) {
    throw new Error(`Error al registrar pago: ${error.message}`);
  }
  return id as string;
}

/** Reverse all recorded payments of a participant (kept in the ledger as REVERSED). */
export async function reversePayment(data: ReversePaymentData): Promise<void> {
  const { error } = await db.rpc('reverse_event_payments', {
    p_participant_id: data.participant_id,
    p_reason: data.reversal_reason || 'Reversión sin motivo especificado',
  });
  if (error) {
    throw new Error(`Error al revertir pago: ${error.message}`);
  }
}

export async function linkPaymentReceipt(paymentId: string, receiptId: string): Promise<void> {
  const { error } = await db.rpc('link_event_payment_receipt', { p_payment_id: paymentId, p_receipt_id: receiptId });
  if (error) {
    throw new Error(`Error al vincular recibo: ${error.message}`);
  }
}

/** Pay the outstanding balance (calculated_price − paid) for each participant. */
export async function bulkRecordPayments(data: {
  participant_ids: string[];
  method?: EventPaymentMethod | null;
}): Promise<BulkResult> {
  const result: BulkResult = { success: 0, failed: 0, errors: [] };

  const { data: rows, error } = await supabase
    .from('event_participants')
    .select('id, calculated_price, payment_amount, payment_status')
    .in('id', data.participant_ids);
  if (error) throw new Error(`Error al cargar participantes: ${error.message}`);

  for (const p of rows ?? []) {
    const outstanding = Math.max(0, Number(p.calculated_price ?? 0) - Number(p.payment_amount ?? 0));
    if (p.payment_status === 'PAID' || outstanding <= 0) continue;
    try {
      await recordPayment({ participant_id: p.id, payment_amount: outstanding, method: data.method });
      result.success++;
    } catch (err) {
      result.failed++;
      result.errors.push(`${p.id}: ${err instanceof Error ? err.message : 'Error'}`);
    }
  }
  return result;
}

export async function bulkRecordPaymentsForEvent(event_id: string): Promise<BulkResult> {
  const { data, error } = await supabase
    .from('event_participants')
    .select('id')
    .eq('event_id', event_id)
    .in('payment_status', ['PENDING', 'PARTIAL', 'REVERSED']);
  if (error) throw new Error(`Error al cargar participantes pendientes: ${error.message}`);
  if (!data?.length) return { success: 0, failed: 0, errors: [] };
  return bulkRecordPayments({ participant_ids: data.map((p) => p.id) });
}

export async function bulkReversePayments(participant_ids: string[], reversal_reason?: string): Promise<BulkResult> {
  const result: BulkResult = { success: 0, failed: 0, errors: [] };
  for (const participant_id of participant_ids) {
    try {
      await reversePayment({ participant_id, reversal_reason });
      result.success++;
    } catch (err) {
      result.failed++;
      result.errors.push(`${participant_id}: ${err instanceof Error ? err.message : 'Error'}`);
    }
  }
  return result;
}

// ===================
// QUERIES
// ===================

const PAYMENT_SELECT = `
  *,
  participant:participant_id (
    id, calculated_price, minister_id, public_token, registration_data,
    minister:minister_id ( full_name, adb_code, dni, cell_phone ),
    guest:guest_id ( full_name, dni, phone )
  ),
  recorder:recorded_by ( full_name ),
  receipt:receipt_id ( id, number, year, status )
`;

export async function getEventPayments(eventId: string): Promise<EventPayment[]> {
  const { data, error } = await db
    .from('event_payments')
    .select(PAYMENT_SELECT)
    .eq('event_id', eventId)
    .order('paid_at', { ascending: false });
  if (error) throw new Error(`Error al cargar pagos: ${error.message}`);
  return (data ?? []) as EventPayment[];
}

export async function getParticipantPayments(participantId: string): Promise<EventPayment[]> {
  const { data, error } = await db
    .from('event_payments')
    .select(PAYMENT_SELECT)
    .eq('participant_id', participantId)
    .order('paid_at', { ascending: false });
  if (error) throw new Error(`Error al cargar pagos: ${error.message}`);
  return (data ?? []) as EventPayment[];
}

// ===================
// PAYMENT STATISTICS
// ===================

export interface PaymentStats {
  total: number;
  paid: number;
  partial: number;
  pending: number;
  reversed: number;
  total_paid_amount: number;
  total_pending_amount: number;
  payment_rate: number; // percentage of paid vs total
}

export async function getPaymentStats(event_id: string): Promise<PaymentStats> {
  const { data, error } = await supabase
    .from('event_participants')
    .select('payment_status, payment_amount, calculated_price')
    .eq('event_id', event_id);

  if (error) {
    throw new Error(`Error fetching payment stats: ${error.message}`);
  }

  const total = data.length;
  const count = (s: string) => data.filter((p) => p.payment_status === s).length;
  const paid = count('PAID');
  return {
    total,
    paid,
    partial: count('PARTIAL'),
    pending: count('PENDING'),
    reversed: count('REVERSED'),
    total_paid_amount: data.reduce((sum, p) => sum + Number(p.payment_amount || 0), 0),
    total_pending_amount: data
      .filter((p) => p.payment_status !== 'PAID')
      .reduce((sum, p) => sum + Math.max(0, Number(p.calculated_price || 0) - Number(p.payment_amount || 0)), 0),
    payment_rate: total > 0 ? Math.round((paid / total) * 100) : 0,
  };
}
